import axios from 'axios';
import i18n from '../i18n';
import { API_URL } from 'config';
import { ApiError } from './errors';

export { ApiError };

export function normalizeError(error) {
  if (error instanceof ApiError) return error;

  if (axios.isCancel(error)) {
    return new ApiError({ code: 'REQUEST_CANCELED', message: error.message || 'Canceled' });
  }

  const res = error && error.response;
  if (res) {
    const body = res.data;
    // Server envelope: { success:false, code, message, details }
    if (body && typeof body === 'object' && body.success === false && body.code) {
      return new ApiError({
        status: res.status,
        code: body.code,
        message: body.message || i18n.t('errors.generic'),
        details: body.details === undefined ? null : body.details,
      });
    }
    // A proxy / gateway page or anything else that is not our envelope.
    return new ApiError({
      status: res.status,
      code: res.status >= 500 ? 'SERVER_UNAVAILABLE' : 'UNEXPECTED_RESPONSE',
      message: i18n.t(res.status >= 500 ? 'errors.server' : 'errors.generic'),
    });
  }

  if (error && error.code === 'ECONNABORTED') {
    return new ApiError({ code: 'TIMEOUT', message: i18n.t('errors.timeout') });
  }
  return new ApiError({ code: 'NETWORK_ERROR', message: i18n.t('errors.network') });
}

// Public endpoints only: no cookies, no credentials, no CSRF.
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  timeout: 15000,
  headers: { Accept: 'application/json' },
});

api.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (body && typeof body === 'object' && body.success === false) {
      // 2xx with a failure envelope: treat as an error too.
      return Promise.reject(normalizeError({ response }));
    }
    if (!body || typeof body !== 'object' || body.success !== true) {
      return Promise.reject(
        normalizeError({ response: { status: response.status, data: body } })
      );
    }
    return response;
  },
  (error) => Promise.reject(normalizeError(error))
);

/**
 * Perform a request and unwrap the success envelope.
 *   const { value, count } = await request({ url: '/api/commerce/v1/public/store' });
 * Rejects with ApiError.
 */
export async function request(config) {
  const { data } = await api.request(config);
  return { value: data.value, count: data.count };
}

export default api;
